import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { revenue, type RevenueDimension, type RevenueGranularity } from "@/api/endpoints";
import { Spinner } from "@/components/shared/Spinner";
import { StackedTotalTooltip } from "./ChartTooltip";

const REVENUE_REFETCH_MS = 60 * 60 * 1000;

interface Props {
  dimension: RevenueDimension;
  start: string;
  end: string;
  gran: RevenueGranularity | "auto";
  onGranChange: (g: RevenueGranularity | "auto") => void;
}

const COLORS = [
  "#2563eb", "#059669", "#a855f7", "#f59e0b", "#dc2626",
  "#0891b2", "#65a30d", "#db2777", "#7c3aed", "#0d9488",
  "#94a3b8", // last = "Other"
];

const GRANS: { key: RevenueGranularity; label: string }[] = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

export function autoGranularity(start: string, end: string): RevenueGranularity {
  const days = Math.round(
    (new Date(end).getTime() - new Date(start).getTime()) / (24 * 60 * 60 * 1000),
  );
  if (days <= 35) return "daily";
  if (days <= 180) return "weekly";
  return "monthly";
}

function formatPeriod(p: string, gran: RevenueGranularity): string {
  if (gran === "daily") return p.slice(5);            // "MM-DD"
  if (gran === "weekly") return `wk ${p.slice(5)}`;   // "wk MM-DD"
  return p.slice(0, 7);                                // "YYYY-MM"
}

const fmtCompact = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

export function RevenueChart({ dimension, start, end, gran, onGranChange }: Props) {
  const effectiveGran: RevenueGranularity = gran === "auto" ? autoGranularity(start, end) : gran;

  const q = useQuery({
    queryKey: ["revenue", "series", dimension, effectiveGran, start, end],
    queryFn: () => revenue.series(dimension, effectiveGran, start, end, 10),
    refetchInterval: REVENUE_REFETCH_MS,
  });

  // Series visibility — clickable legend toggles, shift-click isolates one.
  // Reset whenever the dimension changes so we don't carry "Acme" hidden over
  // from "By tenant" into "By GPU type" where it doesn't exist.
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  useEffect(() => {
    setHidden(new Set());
  }, [dimension]);

  const allLabels = useMemo(
    () => (q.data?.groups ?? []).map((g) => g.label),
    [q.data],
  );

  const handleLegendClick = (entry: { value?: string }, _i: number, ev?: { shiftKey?: boolean }) => {
    const label = entry.value;
    if (!label) return;
    setHidden((prev) => {
      // Shift-click → isolate: hide all *except* this one. Click again on the
      // same label (when only it is visible) → un-isolate (show all).
      if (ev?.shiftKey) {
        const onlyMe = new Set(allLabels.filter((l) => l !== label));
        const isAlreadyIsolated =
          prev.size === onlyMe.size && [...prev].every((l) => onlyMe.has(l));
        return isAlreadyIsolated ? new Set() : onlyMe;
      }
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const showAll = () => setHidden(new Set());
  const anyHidden = hidden.size > 0;

  const chartData = useMemo(() => {
    const data = q.data;
    if (!data) return [];
    return data.periods.map((p, i) => {
      const row: Record<string, number | string> = {
        period: formatPeriod(p, data.granularity),
      };
      for (const g of data.groups) {
        row[g.label] = Number(g.values[i] ?? 0);
      }
      return row;
    });
  }, [q.data]);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:bg-slate-900 dark:border-slate-800">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-baseline gap-3">
          <h2 className="text-sm font-medium">Revenue over time</h2>
          {q.isFetching && (
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
              <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-brand-500" />
              Updating…
            </span>
          )}
          {anyHidden && (
            <button
              onClick={showAll}
              className="text-xs text-brand-700 hover:underline"
              title={`${hidden.size} series hidden — click to show all`}
            >
              Show all ({allLabels.length - hidden.size}/{allLabels.length})
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 text-xs">
          <button
            key="auto"
            onClick={() => onGranChange("auto")}
            className={
              gran === "auto"
                ? "rounded-md bg-slate-900 px-2 py-1 text-white dark:bg-white dark:text-slate-900"
                : "rounded-md px-2 py-1 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            }
            title="Auto-pick granularity from window length"
          >
            Auto
          </button>
          {GRANS.map((g) => (
            <button
              key={g.key}
              onClick={() => onGranChange(g.key)}
              className={
                gran === g.key
                  ? "rounded-md bg-slate-900 px-2 py-1 text-white dark:bg-white dark:text-slate-900"
                  : "rounded-md px-2 py-1 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
              }
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-72">
        {q.isLoading ? (
          <Spinner />
        ) : q.error ? (
          <div className="text-sm text-red-700">{(q.error as Error).message}</div>
        ) : !q.data || q.data.periods.length === 0 ? (
          <div className="text-sm text-slate-500">No revenue data in this window.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="period" fontSize={11} interval="preserveStartEnd" />
              <YAxis fontSize={11} tickFormatter={(v) => `$${fmtCompact.format(v)}`} />
              <Tooltip
                content={((props: any) => (
                  <StackedTotalTooltip {...props} hidden={hidden} />
                )) as never}
              />
              <Legend
                wrapperStyle={{ fontSize: 12, cursor: "pointer", userSelect: "none" }}
                onClick={handleLegendClick as never}
                formatter={(value) => (
                  <span
                    style={{
                      opacity: hidden.has(String(value)) ? 0.4 : 1,
                      textDecoration: hidden.has(String(value)) ? "line-through" : undefined,
                    }}
                    title="Click to toggle · shift-click to isolate"
                  >
                    {value}
                  </span>
                )}
              />
              {q.data.groups.map((g, i) => (
                <Bar
                  key={g.label}
                  dataKey={g.label}
                  stackId="rev"
                  fill={COLORS[i % COLORS.length]}
                  isAnimationActive={false}
                  hide={hidden.has(g.label)}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
